import React from 'react';
import { Box, Button, CircularProgress, Typography, styled } from '@mui/material';
import { ReactComponent as PersonWalkIcon } from 'assets/icons/PersonWalkIcon.svg';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { StepDetailsDialogState, useStepDetails } from 'features/checklist/hooks/useStepDetails';
import { StepDetailsConfirmationDialog } from 'components/StepsDetailsScreen/StepDetailsConfirmationDialog';
import { PhysicalActivityStat } from 'services/statsService';
import { WidgetCard } from 'components/WidgetCard/WidgetCard';
import { formatLocalDateString, parseLocalDateString } from 'utils';
import { ReactComponent as EditIcon } from './Edit.svg';
import { StepCountChart } from './StepCountChart';

const DAYS_IN_WEEK = 7;
const MILES_PER_STEPS = 2000;

const Stats = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingBottom: theme.spacing(1.5),
  },
}));

const Stat = styled(Box)(({ theme }) => ({
  flex: 1,
  textAlign: 'center',
  '& + &': { borderLeft: `1px solid ${theme.palette.grey[500]}` },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  ...theme.typography.title3,
  color: theme.palette.grey[800],
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title1,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.subheadline,
  color: theme.palette.grey[800],
  [theme.breakpoints.up('sm')]: theme.typography.headline,
}));

const GoalText = styled(Typography)(({ theme }) => ({
  ...theme.typography.callout,
  color: theme.palette.primary.main,
  [theme.breakpoints.up('sm')]: theme.typography.title3,
}));

const EditButton = styled(Button)(({ theme }) => ({
  minWidth: 32,
  padding: theme.spacing(0.5),
  '& svg': { width: 13, height: 17 },
  [theme.breakpoints.up('sm')]: { '& svg': { width: 15, height: 20 } },
}));

const CenteredState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(3),
}));

const weekEndingOn = (end: Date) =>
  Array.from({ length: DAYS_IN_WEEK }, (_, index) => {
    const date = new Date(end);
    date.setDate(end.getDate() - (DAYS_IN_WEEK - 1 - index));
    return formatLocalDateString(date);
  });

// 7 consecutive days ending on the latest stat (or today when there are none), gaps filled with 0 steps.
export const buildStepWeek = (stats: PhysicalActivityStat[], today: Date = new Date()) => {
  const byDate = new Map(stats.map((stat) => [stat.date, stat]));
  const latest = stats.reduce<PhysicalActivityStat | null>(
    (current, stat) => (current == null || stat.date > current.date ? stat : current),
    null,
  );
  const end = latest ? parseLocalDateString(latest.date) : today;
  return weekEndingOn(end).map(
    (date) =>
      byDate.get(date) ?? {
        active_goal_steps: 0,
        active_steps: 0,
        date,
        elapsed_seconds: 0,
        goal_seconds: 0,
        passive_goal_steps: latest?.passive_goal_steps ?? 0,
        passive_steps: 0,
        steps: 0,
      },
  );
};

export const getChartMaximum = (week: PhysicalActivityStat[], goal: number) => {
  const highest = Math.max(goal, ...week.map((stat) => stat.passive_steps));
  return Math.max(2000, Math.ceil((highest + 500) / 2000) * 2000);
};

export interface StepCountWidgetViewProps {
  physicalActivityStats: PhysicalActivityStat[];
  isLoading?: boolean;
  isError?: boolean;
}

export const StepCountWidgetView = ({
  physicalActivityStats,
  isLoading = false,
  isError = false,
}: StepCountWidgetViewProps) => {
  const { translation } = useTranslationContext();
  const hasData = !isLoading && !isError;
  const week = buildStepWeek(physicalActivityStats);
  // A new goal only starts tomorrow, so the displayed goal always comes from the stats.
  const goal = week[week.length - 1]?.passive_goal_steps ?? 0;
  // The goal editor is the home page's; only the goal half of the task is used.
  const { dialogState, stepGoalInput, setStepGoalInput, ...dialogHandlers } = useStepDetails({
    stepGoal: goal,
    recordedSteps: null,
  } as PhysicalActivityTask);
  const chartMaximum = getChartMaximum(week, goal);
  const totalSteps = week.reduce((total, stat) => total + stat.passive_steps, 0);
  const average = Math.round(totalSteps / DAYS_IN_WEEK);

  return (
    <>
      <WidgetCard
        icon={
          <Box display="flex" color="secondary.main">
            <PersonWalkIcon />
          </Box>
        }
        title={translation('step_count_settings')}
        headerAction={
          <Box display="flex" alignItems="center">
            <GoalText>Goal: {goal.toLocaleString()}</GoalText>
            <EditButton
              aria-label="Edit daily step goal"
              onClick={dialogHandlers.handleEditGoalClicked}
            >
              <EditIcon />
            </EditButton>
          </Box>
        }
      >
        {isLoading && (
          <CenteredState>
            <CircularProgress aria-label={translation('step_count_settings')} />
          </CenteredState>
        )}
        {!isLoading && isError && (
          <CenteredState>
            <Typography variant="footnote" role="alert">
              {translation('data_unable_to_load')}
            </Typography>
          </CenteredState>
        )}
        {hasData && (
          <>
            {/* On mobile the y labels sit partly in the card padding, as in the design. */}
            <Box role="img" aria-label="Seven day step count chart" sx={{ ml: { xs: -1, sm: 0 } }}>
              <StepCountChart week={week} goal={goal} maximum={chartMaximum} />
            </Box>
            <Stats>
              <Stat>
                <StatValue>{average}</StatValue>
                <StatLabel>7 day average</StatLabel>
              </Stat>
              <Stat>
                <StatValue>{(totalSteps / MILES_PER_STEPS).toFixed(1)} mi</StatValue>
                <StatLabel>7 day total</StatLabel>
              </Stat>
            </Stats>
          </>
        )}
      </WidgetCard>
      <StepDetailsConfirmationDialog
        open={dialogState !== StepDetailsDialogState.NONE}
        dialogState={dialogState}
        stepCountInput={0}
        setStepCountInput={() => {}}
        variant="fullscreen"
        stepGoalInput={stepGoalInput}
        setStepGoalInput={setStepGoalInput}
        handleNo={dialogHandlers.handleDialogNo}
        handleYes={dialogHandlers.handleDialogYes}
        yesButtonDisabled={!dialogHandlers.isDialogYesButtonEnabled}
      />
    </>
  );
};
